/**
 * Écran de fin — affiché à quiconque est allé au bout de l'épreuve.
 *
 * 🩸 Son en-tête a menti deux fois, sur la même phrase. Il annonçait « seuil
 * ATTEINT — cet écran ne s'affiche que quand `reached` est vrai » : faux
 * depuis le 18-08-2026, où le seuil a disparu (la conversation se LIT, elle ne
 * se note pas ; c'est Kayla qui tranche sur la fiche). Et il décrivait un
 * `claim` « qui re-vérifie le seuil server-to-server » : faux depuis le 19-08,
 * où la porte de l'invitation est devenue le DÉPÔT DU PROFIL.
 *
 * AUCUN score n'y est montré, et il n'y en a plus à montrer.
 *
 * ⚠️ L'invitation ne se joue plus ICI : elle est partie bien avant, dès le
 * formulaire (cf. `RegieTestCandidatePage`, `ECRANS_PROFIL_DEPOSE`). Le
 * `claim` de cet écran est donc un FILET, pas la source — en mode "ensure" il
 * ne renvoie rien si l'invitation est déjà en attente.
 *
 * 🩸 ET SON CORPS A MENTI UNE TROISIÈME FOIS, JUSQU'AU 20-08-2026. Il
 * annonçait, en toutes lettres et quel que soit l'état : « Ton invitation part
 * par e-mail […] clique le lien dedans pour créer ton compte », « vérifie tes
 * spams ». Or l'ordre du parcours est activer → Discord → LE TEST → le
 * cockpit : quand on arrive ici, on est déjà membre dans la quasi-totalité des
 * cas — la bannière le disait elle-même deux lignes plus bas (« Ton compte est
 * déjà activé »), sur le même écran, en même temps.
 *
 * ⛔ Et le seul bouton offert était « Je n'ai rien reçu — renvoyer l'e-mail ».
 * Pour un membre déjà entré, `claim` s'arrête sur `deja_membre` AVANT tout
 * envoi (cf. `convex/candidateInvite.ts`) : rien ne part, et l'écran affichait
 * quand même « Renvoyé — regarde ta boîte (et tes spams) ». Un e-mail qui ne
 * partira jamais, annoncé comme parti. Kayla, le 20-08 : « il y a plein de
 * gens qui me parlent d'un mail qu'ils ne reçoivent pas […] ils me font tous
 * des tickets pour rien. »
 *
 * ✅ CE QUI LE REMPLACE — la même règle que `EcranCandidatureDeposee`, qui l'a
 * apprise la nuit d'avant : le GESTE devant, l'e-mail derrière en filet.
 *   - déjà membre  → on le renvoie sur le cockpit, et on ne parle pas d'e-mail ;
 *   - lien rendu   → bouton « Activer mon compte » (le lien MÊME de l'e-mail,
 *                    franchi une porte plus tôt), et on ne parle pas d'e-mail ;
 *   - aucun lien   → là, et là seulement, l'e-mail EST le chemin : il faut
 *                    l'annoncer, avec l'adresse, les spams, et le renvoi.
 *
 * Le bouton de renvoi ne survit donc que dans le dernier cas (et sur erreur,
 * comme réessai) — c'est le seul où il envoie vraiment quelque chose.
 *
 * En mode maquette (?mock=1, DEV) il n'y a pas de session Convex : on affiche
 * l'état « envoyé » simulé.
 */
import { useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { ConvexError } from "convex/values";

import { api as convexApi } from "@/../convex/_generated/api";
import { Alerte, Button, Spinner } from "@/shared/ui";
import { ArrowRight, Check, MailPlus } from "@/shared/ui/icons";

import { IS_MOCK_MODE } from "../api";

const RESEND_COOLDOWN_MS = 30_000;

type InviteState =
  | { kind: "sending" }
  /** `deja_membre` = l'invitation est deja acceptee : plus rien a envoyer,
   *  et surtout pas un « Invitation envoyee » qui ferait attendre un e-mail
   *  qui ne partira pas.
   *
   *  `acceptUrl` = le lien d'acceptation WorkOS, celui que porte l'e-mail.
   *  `null` quand WorkOS ne l'a pas rendu : aucun bouton plutot qu'un lien
   *  mort, et l'e-mail redevient alors le seul chemin. */
  | {
      kind: "ok";
      method: "sent" | "resent" | "already" | "deja_membre";
      acceptUrl: string | null;
    }
  | { kind: "error"; message: string };

interface EndScreenProps {
  firstName: string | null;
  email: string | null;
  token: string;
}

export function EndScreen({ firstName, email, token }: EndScreenProps) {
  const claim = useAction(convexApi.candidateInvite.claim);
  const [invite, setInvite] = useState<InviteState>(
    IS_MOCK_MODE
      ? {
          kind: "ok",
          method: "sent",
          acceptUrl:
            "https://exemple.authkit.app/invite?invitation_token=maquette",
        }
      : { kind: "sending" },
  );
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const ensured = useRef(false);

  async function runClaim(mode: "ensure" | "resend") {
    setInvite({ kind: "sending" });
    try {
      const result = await claim({ token, mode });
      setInvite({
        kind: "ok",
        method: result.status,
        acceptUrl: result.acceptUrl,
      });
      if (mode === "resend") setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);
    } catch (err) {
      const reason =
        err instanceof ConvexError
          ? (err.data as { reason?: string } | undefined)?.reason
          : undefined;
      setInvite({
        kind: "error",
        message: reason ?? "L'envoi n'a pas abouti — réessaie dans un instant.",
      });
    }
  }

  useEffect(() => {
    if (IS_MOCK_MODE || ensured.current) return;
    ensured.current = true;
    void runClaim("ensure");
    // `runClaim` est stable pour la vie de l'écran (token/claim ne changent
    // pas) — l'effet ne doit tourner qu'une fois, gardé par la ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = invite.kind === "sending";
  const cooling = cooldownUntil > Date.now();

  const dejaMembre = invite.kind === "ok" && invite.method === "deja_membre";
  /** Le lien d'activation, quand il en reste un À FRANCHIR. Un membre déjà
   *  entré a consommé le sien : le lui offrir serait un bouton mort. */
  const urlActivation =
    invite.kind === "ok" && !dejaMembre ? invite.acceptUrl : null;
  /** ⚠️ LA CONDITION QUI TIENT TOUT L'ÉCRAN : l'e-mail n'est le chemin que
   *  s'il ne reste rien d'autre. Ni compte déjà ouvert, ni bouton. */
  const emailEstLeChemin =
    invite.kind === "ok" && !dejaMembre && urlActivation === null;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-8 text-center">
      <Check aria-hidden weight="bold" className="anim-pop-in h-11 w-11 text-valide" />

      <h2
        className="anim-fade-up text-[28px] font-extrabold leading-tight tracking-tight text-fg"
        style={{ animationDelay: "120ms" }}
      >
        Félicitations{firstName ? `, ${firstName}` : ""} ! 🎉
      </h2>

      <div
        className="anim-fade-up flex max-w-[360px] flex-col gap-2.5 text-[16px] leading-relaxed text-muted"
        style={{ animationDelay: "220ms" }}
      >
        {/* Le cas NORMAL au bout de l'épreuve : il est entré AVANT de la
            passer. Aucun mot d'e-mail — c'est très exactement ce qui le
            faisait ouvrir un ticket. */}
        {dejaMembre && (
          <>
            <p>
              <b className="text-fg">Tu n'as aucun e-mail à attendre</b> : ton
              accès est déjà ouvert.
            </p>
            <p className="text-[14px]">
              Ton test est enregistré — retourne sur le cockpit, il s'ouvre pour
              toi.
            </p>
          </>
        )}

        {/* Il reste une porte, et elle est ICI, sous ses yeux. */}
        {urlActivation && (
          <>
            <p>
              Il te reste une étape :{" "}
              <b className="text-fg">activer ton compte</b>. Clique le bouton
              ci-dessous — c'est tout.
            </p>
            <p className="text-[14px]">
              <b className="text-fg">Tu n'as aucun e-mail à attendre.</b> On t'en
              a aussi envoyé un{email ? ` à ${email}` : ""}, au cas où tu
              fermerais cette page.
            </p>
          </>
        )}

        {/* ⛔ L'AUTRE BORD : sans bouton, l'e-mail est le seul chemin qui
            reste — il faut donc l'annoncer, avec l'adresse et le réflexe des
            spams. Le retirer ici laisserait le candidat sans rien. */}
        {emailEstLeChemin && (
          <>
            <p>
              📩 Ton invitation part par e-mail
              {email ? (
                <>
                  {" "}
                  à <b className="text-fg">{email}</b>
                </>
              ) : (
                <> à l'adresse donnée au début du test</>
              )}
              . Clique le lien dedans pour créer ton compte.
            </p>
            <p className="text-[14px]">
              Rien au bout de quelques minutes ? <b className="text-fg">Vérifie tes spams</b> (courriers
              indésirables).
            </p>
          </>
        )}

        {/* ⛔ Un envoi qui a échoué ne s'annonce pas comme parti : il
            surveillerait une boîte vide. */}
        {invite.kind === "error" && (
          <p>Ta candidature est bien enregistrée — on te recontacte.</p>
        )}
      </div>

      {/* État de l'envoi — l'Alerte de la librairie (elle pose `role="status"`, ou `alert` sur un échec). UNE seule
          Alerte dont le ton change : le même nœud reste dans la page, le lecteur d'écran annonce son nouveau texte. */}
      <div
        className="anim-fade-up flex w-full max-w-[320px] justify-center"
        style={{ animationDelay: "300ms" }}
      >
        <Alerte
          ton={invite.kind === "ok" ? "ok" : invite.kind === "error" ? "erreur" : "info"}
          taille="sm"
          className="text-left"
          icone={invite.kind === "sending" ? <Spinner taille="sm" decoratif /> : undefined}
          texte={
            invite.kind === "sending"
              ? "Envoi de ton invitation…"
              : invite.kind === "ok"
                ? invite.method === "deja_membre"
                  ? "Ton compte est déjà activé"
                  : invite.method === "resent"
                    ? "Invitation renvoyée"
                    : "Invitation envoyée"
                : invite.message
          }
        />
      </div>

      {/* Le geste principal — il se fait MAINTENANT, sur l'appareil qui vient
          de finir l'épreuve. C'est la même porte que l'e-mail, franchie une
          étape plus tôt, jamais un second parcours. */}
      {urlActivation && (
        <Button
          href={urlActivation}
          size="xl"
          variant="accent"
          className="anim-fade-up w-full max-w-[320px] justify-center"
          style={{ animationDelay: "380ms" }}
          iconRight={<ArrowRight aria-hidden className="h-5 w-5" />}
        >
          Activer mon compte
        </Button>
      )}

      {/* Déjà entré : le seul geste qui lui reste est de revenir sur le
          cockpit, qui s'ouvre maintenant que le test est fait (cf.
          `MemberProfileGate`). */}
      {dejaMembre && (
        <Button
          href="/"
          size="xl"
          variant="accent"
          className="anim-fade-up w-full max-w-[320px] justify-center"
          style={{ animationDelay: "380ms" }}
          iconRight={<ArrowRight aria-hidden className="h-5 w-5" />}
        >
          Retourner sur le cockpit
        </Button>
      )}

      {/* ⛔ Le renvoi ne survit QUE là où un e-mail part vraiment : quand il
          est le seul chemin, ou en réessai après un échec. Ailleurs il
          annonçait « Renvoyé — regarde ta boîte » sans que rien ne parte. */}
      {(emailEstLeChemin || invite.kind === "error") && (
        <Button
          size="lg"
          variant="secondary"
          disabled={busy || cooling}
          onClick={() => void runClaim("resend")}
          className="anim-fade-up min-h-12 w-full max-w-[320px] justify-center py-2.5 text-center text-[14px] leading-snug"
          style={{ animationDelay: "380ms" }}
        >
          <MailPlus aria-hidden className="h-4 w-4" />
          {cooling ? "Renvoyé — regarde ta boîte (et tes spams)" : "Je n'ai rien reçu — renvoyer l'e-mail"}
        </Button>
      )}

      {!dejaMembre && (
        <p
          className="anim-fade-up max-w-[320px] text-[13px] leading-relaxed text-muted"
          style={{ animationDelay: "460ms" }}
        >
          {urlActivation
            ? "Ensuite on te demandera de connecter ton Discord — et tu rejoins l'équipe."
            : "Ensuite : tu crées ton compte, tu connectes ton Discord — et tu rejoins l'équipe. Tu peux fermer cette page."}
        </p>
      )}
    </div>
  );
}
